// Client-side analytics derivations mirroring RestaurantRevenueViewModel +
// RestaurantDailyEarningsViewModel from the iOS app. All calculations run on
// the orders returned by listenToRestaurantOrders (no extra queries).
import { OrderStatus, restaurantPayout, toDate } from '../../shared/models';

export const TIMEFRAMES = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function dayKey(d) {
  const x = startOfDay(d);
  return `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
}

// Mirrors iOS filteredOrders(for:):
//   day   -> today (start of day -> now)
//   week  -> start of the current week (Sunday) -> now
//   month -> last 30 days -> now
//   year  -> start of current calendar year -> now
export function timeframeStart(timeframe, now = new Date()) {
  switch (timeframe) {
    case 'day':
      return startOfDay(now);
    case 'week': {
      const s = startOfDay(now);
      s.setDate(s.getDate() - s.getDay()); // back to Sunday
      return s;
    }
    case 'month': {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return d;
    }
    case 'year':
    default:
      return new Date(now.getFullYear(), 0, 1);
  }
}

// Previous-period start used for growth metrics: an equal-length window
// ending where the current window begins (iOS uses a fixed -2x offset).
export function previousTimeframeStart(timeframe, now = new Date()) {
  const current = timeframeStart(timeframe, now);
  return new Date(current.getTime() - (now.getTime() - current.getTime()));
}

export const isCancelled = (o) =>
  o.status === OrderStatus.CANCELLED || (o.rawStatus || '').toLowerCase() === 'cancelled';

// Payment states where money was never collected — these orders must not
// count toward revenue. awaiting_payment covers JAD authorizations that never
// completed (also written into `status` for pre-auth orders).
export const NON_REVENUE_PAYMENT_STATUSES = ['awaiting_payment', 'failed', 'voided', 'refunded'];

export const isUnpaid = (o) =>
  NON_REVENUE_PAYMENT_STATUSES.includes((o.paymentStatus || '').toLowerCase()) ||
  (o.rawStatus || '').toLowerCase() === 'awaiting_payment';

// Revenue = paid, non-cancelled orders only.
export const isRevenueOrder = (o) => !isCancelled(o) && !isUnpaid(o);

export const isCompleted = (o) =>
  o.status === OrderStatus.DELIVERED || o.status === OrderStatus.COMPLETED ||
  ['delivered', 'completed'].includes((o.rawStatus || '').toLowerCase());

// iOS RestaurantDailyEarningsView counts only accepted orders
// (confirmed → completed); pending and cancelled never hit the calendar.
export const isEarningsCounted = (o) => {
  const s = (o.rawStatus || o.status || '').toLowerCase();
  return s !== 'pending' && s !== 'cancelled' && s !== 'awaiting_payment' && s !== '';
};

// Restaurant share of an order: restaurantPayout field, else 90% of subtotal.
export const payoutOf = (o) => restaurantPayout(o);

export function ordersInTimeframe(orders, timeframe, now = new Date()) {
  const start = timeframeStart(timeframe, now);
  return orders
    .filter((o) => o.createdAt && o.createdAt >= start && o.createdAt <= now)
    .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
}

// Summary stats for the filtered window. Revenue/avg exclude cancelled + unpaid orders.
export function computeSummary(orders) {
  const active = orders.filter(isRevenueOrder);
  const revenue = active.reduce((s, o) => s + payoutOf(o), 0);
  const completed = active.filter(isCompleted).length;
  return {
    revenue,
    orders: active.length,
    avgOrder: active.length ? revenue / active.length : 0,
    cancelled: orders.filter(isCancelled).length,
    completed,
    completionRate: orders.length ? (active.filter(isCompleted).length / orders.length) * 100 : 0,
  };
}

// Growth vs the equivalent previous period (iOS calculateGrowthMetrics).
export function computeGrowth(allOrders, timeframe, now = new Date()) {
  const currentStart = timeframeStart(timeframe, now);
  const previousStart = previousTimeframeStart(timeframe, now);
  const active = allOrders.filter((o) => isRevenueOrder(o) && o.createdAt);
  const cur = active.filter((o) => o.createdAt >= currentStart && o.createdAt <= now);
  const prev = active.filter((o) => o.createdAt >= previousStart && o.createdAt < currentStart);
  const curRev = cur.reduce((s, o) => s + payoutOf(o), 0);
  const prevRev = prev.reduce((s, o) => s + payoutOf(o), 0);
  return {
    revenueGrowth: prevRev > 0 ? ((curRev - prevRev) / prevRev) * 100 : (curRev > 0 ? 100 : 0),
    orderGrowth: prev.length > 0 ? ((cur.length - prev.length) / prev.length) * 100 : (cur.length > 0 ? 100 : 0),
  };
}

// Revenue grouped by calendar day, ascending (iOS revenueData).
export function revenueByDay(orders) {
  const map = new Map();
  for (const o of orders) {
    if (!isRevenueOrder(o) || !o.createdAt) continue;
    const k = dayKey(o.createdAt);
    const e = map.get(k) || { date: startOfDay(o.createdAt), revenue: 0, orders: 0 };
    e.revenue += payoutOf(o);
    e.orders += 1;
    map.set(k, e);
  }
  if (map.size === 0) return [];

  // One entry per calendar day from the first sale (or 7 days back) through
  // today, with $0 for quiet days — so a single day of sales still draws a curve.
  const days = [...map.values()].sort((a, b) => a.date - b.date);
  const first = days[0].date;
  const last = days[days.length - 1].date;
  const today = startOfDay(new Date());
  const end = last > today ? last : today;
  const weekBack = new Date(end);
  weekBack.setDate(weekBack.getDate() - 6);
  const start = first < weekBack ? first : weekBack;

  const filled = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const k = dayKey(d);
    filled.push(map.get(k) || { date: new Date(d), revenue: 0, orders: 0 });
  }
  return filled.map((d) => ({
    ...d,
    label: d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));
}

// Revenue grouped by hour (0-23), all hours present (iOS hourlyData).
export function hourlySales(orders) {
  const buckets = Array.from({ length: 24 }, (_, h) => ({ hour: h, revenue: 0, orders: 0 }));
  for (const o of orders) {
    if (!isRevenueOrder(o) || !o.createdAt) continue;
    const h = o.createdAt.getHours();
    buckets[h].revenue += payoutOf(o);
    buckets[h].orders += 1;
  }
  return buckets;
}

// Top selling items by quantity (iOS topItems).
export function topItems(orders, limit = 10) {
  const stats = new Map();
  for (const o of orders) {
    if (!isRevenueOrder(o)) continue;
    for (const it of o.orderItems || []) {
      const name = it.menuItemName || it.name || 'Item';
      const e = stats.get(name) || { name, quantity: 0, revenue: 0 };
      e.quantity += it.quantity || 1;
      e.revenue += it.totalPrice ?? (it.price || 0) * (it.quantity || 1);
      stats.set(name, e);
    }
  }
  return [...stats.values()].sort((a, b) => b.quantity - a.quantity).slice(0, limit);
}

// Category performance. Order items don't carry a category, so map
// menuItemId -> category using the restaurant's menu-items (iOS does the same
// via fetchMenuItem). Falls back to 'Other' when unknown.
export function categoryPerformance(orders, menuItems = []) {
  const catByItem = new Map();
  for (const m of menuItems) {
    if (m.id) catByItem.set(m.id, m.category || 'Other');
    if (m.name) catByItem.set(`name:${m.name}`, m.category || 'Other');
  }
  const stats = new Map();
  for (const o of orders) {
    if (!isRevenueOrder(o)) continue;
    for (const it of o.orderItems || []) {
      const cat =
        catByItem.get(it.menuItemId) ||
        catByItem.get(`name:${it.menuItemName || it.name}`) ||
        'Other';
      const e = stats.get(cat) || { name: cat, revenue: 0, orders: 0 };
      e.revenue += it.totalPrice ?? (it.price || 0) * (it.quantity || 1);
      e.orders += 1;
      stats.set(cat, e);
    }
  }
  return [...stats.values()].sort((a, b) => b.revenue - a.revenue).slice(0, 10);
}

// Weekly trends by weekday using ALL orders (not timeframe-filtered), like iOS.
export function weeklyTrends(allOrders) {
  const buckets = WEEKDAY_NAMES.map((w) => ({ week: w, short: w.slice(0, 3), revenue: 0, orders: 0 }));
  for (const o of allOrders) {
    if (!isRevenueOrder(o) || !o.createdAt) continue;
    const d = o.createdAt.getDay();
    buckets[d].revenue += payoutOf(o);
    buckets[d].orders += 1;
  }
  return buckets;
}

// Per-day earnings aggregation for the calendar — accepted orders only
// (iOS excludes pending + cancelled from the daily totals).
export function dailyEarningsMap(orders) {
  const map = new Map();
  for (const o of orders) {
    if (!isRevenueOrder(o) || !isEarningsCounted(o) || !o.createdAt) continue;
    const k = dayKey(o.createdAt);
    const e = map.get(k) || {
      date: startOfDay(o.createdAt),
      totalEarnings: 0,
      orderCount: 0,
      subtotal: 0,
      chefServiceFee: 0,
      deliveryFee: 0,
      tips: 0,
      orders: [],
    };
    e.totalEarnings += payoutOf(o);
    e.orderCount += 1;
    e.subtotal += o.subtotal || 0;
    e.chefServiceFee += o.chefServiceFee || 0;
    e.deliveryFee += o.deliveryFee || 0;
    e.tips += o.tipAmount || 0;
    e.orders.push(o);
    map.set(k, e);
  }
  for (const e of map.values()) {
    e.orders.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
    e.averageOrderValue = e.orderCount ? e.totalEarnings / e.orderCount : 0;
  }
  return map;
}

export const EMPTY_DAY = {
  date: null,
  totalEarnings: 0,
  orderCount: 0,
  subtotal: 0,
  chefServiceFee: 0,
  deliveryFee: 0,
  tips: 0,
  averageOrderValue: 0,
  orders: [],
};

export function earningsForDate(map, date) {
  return map.get(dayKey(date)) || { ...EMPTY_DAY, date: startOfDay(date) };
}

// Chart theme palette mirroring iOS ChartTheme: blue, green, orange, purple, pink, yellow, teal, indigo.
export const CHART_COLORS = [
  '#3B82F6', // blue
  '#22C55E', // green
  '#FF6B35', // orange (brand)
  '#A855F7', // purple
  '#EC4899', // pink
  '#EAB308', // yellow
  '#14B8A6', // teal
  '#6366F1', // indigo
];

export const formatHourLabel = (hour) => {
  if (hour === 0 || hour === 12) return '12';
  return `${hour > 12 ? hour - 12 : hour}`;
};

export const formatHourFull = (hour) => {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  return hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
};

export const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
export const fmtMoneyShort = (n) =>
  Math.abs(n) >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;
